import * as Events from 'events';

import { PopupProps } from './Popup';

export interface PopupItem extends PopupProps {
  id: number;
}

export type PopupArguments = Partial<
  Pick<PopupProps, 'onPressPrimary' | 'onClose'>
> &
  Omit<PopupProps, 'onPressPrimary' | 'onClose' | 'zIndex'>;

type ShowListener = (popupItem: PopupItem) => void;
type HideListener = (
  id: number,
  reason: 'primary' | 'secondary' | 'close',
  value?: string,
) => void;

type PopupEventMap = {
  show: ShowListener;
  hide: HideListener;
};

const DEFAULT_Z_INDEX = 100;

const eventEmitter = new Events.EventEmitter();

let recentPopupId = -1;

const popups: PopupItem[] = [];

const removeListener = <
  EventName extends keyof PopupEventMap,
  EventListener extends PopupEventMap[EventName],
>(
  eventName: EventName,
  listener: EventListener,
) => {
  eventEmitter.removeListener(eventName, listener);
};

const addListener = <
  EventName extends keyof PopupEventMap,
  EventListener extends PopupEventMap[EventName],
>(
  eventName: EventName,
  listener: EventListener,
) => {
  eventEmitter.addListener(eventName, listener);

  return () => {
    removeListener(eventName, listener);
  };
};

const hide = (
  id: number,
  reason: 'primary' | 'secondary' | 'close',
  value?: string | undefined,
  fireOnClose?: boolean,
) => {
  const index = popups.findIndex((popup) => popup.id === id);
  if (index !== -1) {
    const [popupItem] = popups.splice(index, 1);
    if (!popupItem) {
      throw new Error('Unexpected error. popupItem is undefined');
    }
    if (fireOnClose) {
      popupItem.onClose?.(reason, value);
      return index;
    }
  }
  eventEmitter.emit('hide', id, reason, value);
  return index;
};

const show = (args: PopupArguments) => {
  recentPopupId += 1;
  const popupId = recentPopupId;
  const popupItem: PopupItem = {
    ...args,
    onPressPrimary: async (value: string) => {
      const errorMessage = await args.onPressPrimary?.(value);
      if (errorMessage) {
        return errorMessage;
      }
      args.onClose?.('primary', value);
      hide(popupId, 'primary', value);
      return undefined;
    },
    onPressSecondary: (value: string) => {
      args.onPressSecondary?.(value);
      args.onClose?.('secondary', value);
      hide(popupId, 'secondary', value);
    },
    onClose: (reason, value) => {
      args.onClose?.(reason, value);
      hide(popupId, reason, value);
    },
    zIndex: DEFAULT_Z_INDEX + popupId,
    id: popupId,
  };
  popups.push(popupItem);
  eventEmitter.emit('show', popupItem);
  return popupId;
};

const confirm = (args: PopupArguments) =>
  new Promise<boolean>((resolve) => {
    show({
      ...args,
      onPressPrimary: () => {
        resolve(true);
        return undefined;
      },
      onPressSecondary: () => {
        resolve(false);
      },
    });
  });

const form = (args: PopupArguments) =>
  new Promise<string | undefined>((resolve) => {
    show({
      ...args,
      type: 'form',
      onPressPrimary: (value: string) => {
        resolve(value);
        return undefined;
      },
      onPressSecondary: () => {
        resolve(undefined);
      },
    });
  });

export default {
  show,
  hide,
  confirm,
  form,
  addListener,
  removeListener,
};
